import { useEffect, useRef } from 'react';
import { isNativeApp } from '../utils/platform';
import { getCurrentGameTimestamp } from '../config/dateTimeConfig';

/**
 * Listens for native app state changes (background → foreground).
 * When the user returns to the app and the game date has changed
 * (i.e., the daily reset has occurred), reloads the page so all
 * game contexts re-initialize with fresh data.
 */
export function useAppLifecycle(): void {
  const gameDateOnPause = useRef<string>(getCurrentGameTimestamp());

  useEffect(() => {
    if (!isNativeApp()) return;

    let removeListener: (() => void) | null = null;

    (async () => {
      const { App } = await import('@capacitor/app');

      const handle = await App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          // Going to background — snapshot the current game date
          gameDateOnPause.current = getCurrentGameTimestamp();
          return;
        }

        // Returning to foreground — check if the game date has rolled over
        const currentGameDate = getCurrentGameTimestamp();
        if (currentGameDate !== gameDateOnPause.current) {
          // Game date changed while backgrounded — reload to get fresh data
          window.location.reload();
        }
      });

      removeListener = () => handle.remove();
    })();

    return () => {
      removeListener?.();
    };
  }, []);
}
